/**
 * One collapse control for every sidebar (Zac 2026-10-29: the main sidebar's
 * collapse button had slid off the bottom of the window, and the Analytics
 * and Settings menus are second sidebars that could not collapse at all).
 *
 * - `CollapseToggle`: the chevron button at the top of the main sidebar and
 *   of each module menu, so the controls look and behave the same.
 * - `useSubnavCollapsed`: a module menu's collapsed state, remembered per
 *   signed-in user on this device (like the menus' group open/closed state),
 *   with a palette command ("Collapse Analytics menu") as the main sidebar
 *   has ("Collapse navigation").
 */
import { cx, Tooltip } from '@velora/ui';
import { ChevronsLeft, ChevronsRight } from 'lucide-react';
import { useCallback, useState } from 'react';
import { useRegisterCommand } from '../../commands/registry.js';
import { readJson, writeJson } from '../../lib/storage.js';
import { useOptionalSession } from '../../session/session.js';

export interface CollapseToggleProps {
  collapsed: boolean;
  onToggle: () => void;
  /** What collapses, for the label: "navigation" -> "Collapse navigation". */
  label: string;
  className?: string | undefined;
  /** Tooltip side; the rails open to the right. */
  side?: 'top' | 'right' | 'bottom' | 'left';
  'data-testid'?: string;
}

export function CollapseToggle({
  collapsed,
  onToggle,
  label,
  className,
  side = 'right',
  'data-testid': testId,
}: CollapseToggleProps) {
  const name = `${collapsed ? 'Expand' : 'Collapse'} ${label}`;
  return (
    <Tooltip side={side} content={name}>
      <button
        type="button"
        className={cx('vel-collapse-toggle', className)}
        aria-label={name}
        aria-expanded={!collapsed}
        data-collapsed={collapsed ? '' : undefined}
        {...(testId ? { 'data-testid': testId } : {})}
        onClick={onToggle}
      >
        {collapsed ? (
          <ChevronsRight size={16} aria-hidden />
        ) : (
          <ChevronsLeft size={16} aria-hidden />
        )}
      </button>
    </Tooltip>
  );
}

export const SUBNAV_STORE = 'velora.subnav.v1';

/** Module menus that collapse to a rail. */
export type SubnavId = 'analytics' | 'settings';

type Store = Record<string, Partial<Record<SubnavId, boolean>>>;

function readStore(): Store {
  const v = readJson<unknown>(SUBNAV_STORE, {});
  return typeof v === 'object' && v !== null && !Array.isArray(v) ? (v as Store) : {};
}

/** Reads one user's saved state (exported for tests). */
export function readSubnavCollapsed(userKey: string, id: SubnavId): boolean {
  return readStore()[userKey]?.[id] === true;
}

/** Saves one user's state, keeping everyone else's (exported for tests). */
export function writeSubnavCollapsed(userKey: string, id: SubnavId, collapsed: boolean): void {
  const all = readStore();
  const others = Object.entries(all[userKey] ?? {}).filter(([k]) => k !== id);
  all[userKey] = Object.fromEntries(collapsed ? [...others, [id, true]] : others);
  writeJson(SUBNAV_STORE, all);
}

/**
 * A module menu's collapsed state for the signed-in user on this device.
 * `label` names the menu in the palette command ("Analytics menu").
 */
export function useSubnavCollapsed(
  id: SubnavId,
  label: string,
): [collapsed: boolean, setCollapsed: (collapsed: boolean) => void] {
  const session = useOptionalSession();
  const userKey = session?.user.id ?? 'anon';
  const [state, setState] = useState(() => ({
    userKey,
    collapsed: readSubnavCollapsed(userKey, id),
  }));
  // Another person signed in on this device: their own choice.
  const collapsed = state.userKey === userKey ? state.collapsed : readSubnavCollapsed(userKey, id);
  const set = useCallback(
    (next: boolean) => {
      setState({ userKey, collapsed: next });
      writeSubnavCollapsed(userKey, id, next);
    },
    [userKey, id],
  );
  useRegisterCommand({
    id: `shell.subnav.${id}`,
    title: `${collapsed ? 'Expand' : 'Collapse'} ${label}`,
    group: 'Preferences',
    keywords: ['sidebar', 'menu', 'nav', 'collapse', 'expand', 'rail', label.toLowerCase()],
    icon: 'layout-dashboard',
    run: () => set(!collapsed),
  });
  return [collapsed, set];
}
